import { useEffect, useMemo } from 'react';
import { useNodes, useEdges } from '@xyflow/react';
import type { VariableRef } from '@/proto/logiblocks/logiblocks';
import { VariableType } from '@/proto/common';
import type { ReactFlowEdge, ReactFlowNode } from '@/pages/Logiblocks/types';
import type { ValidationError } from '@/pages/Logiblocks/validation';
import {
  getAncestorVariables,
  getAvailableVariables,
  type VariableListItem,
  variableListItemRef,
} from '@/pages/Logiblocks/variables/variableUtils';
import { DropdownVariablePicker } from '@/pages/Logiblocks/variables/DropdownVariablePicker';
import { FieldErrorLabel } from './FieldErrorLabel';
import { FieldLabel } from './FieldLabel';
import styles from '../NodeCustomization.module.css';

const isSameVariable = (a: VariableRef | undefined, b: VariableListItem) => {
  if (!a) return false;
  const option = variableListItemRef(b);
  return (
    a.node_id === option.node_id &&
    a.variable_name === option.variable_name &&
    a.derived_variable_name === option.derived_variable_name
  );
};

export interface ShipmentFieldProps {
  nodeId: string;
  value: VariableRef | undefined;
  onChange: (ref: VariableRef | null) => void;
  disabled?: boolean;
  error?: ValidationError;
  label?: string;
}

/**
 * Shipment picker for node config panels. Only rendered when there's more
 * than one shipment variable available upstream. With exactly one, it's
 * auto-selected and nothing is shown; with none, nothing is shown either and
 * the existing "Shipment" required-field validation blocks publish.
 */
export const ShipmentField = ({
  nodeId,
  value,
  onChange,
  disabled = false,
  error,
  label = 'Shipment',
}: ShipmentFieldProps) => {
  const nodes = useNodes<ReactFlowNode>();
  const edges = useEdges<ReactFlowEdge>();

  const shipmentOptions = useMemo(() => {
    const ancestorVariables = getAncestorVariables(nodeId, nodes, edges);
    return getAvailableVariables(ancestorVariables, [VariableType.SHIPMENT]);
  }, [nodeId, nodes, edges]);

  const onlyOption =
    shipmentOptions.length === 1 ? shipmentOptions[0] : undefined;

  useEffect(() => {
    if (onlyOption && !isSameVariable(value, onlyOption)) {
      onChange(variableListItemRef(onlyOption));
    }
  }, [onlyOption, value, onChange]);

  // 0 or 1 shipment upstream: nothing for the user to choose. With 0, the
  // existing "Shipment" required-field validation still blocks publish even
  // though the field itself is hidden here. With 1, it's auto-selected above.
  if (shipmentOptions.length <= 1) {
    return null;
  }

  return (
    <div className={styles.customizationSection}>
      <FieldLabel required className={styles.sectionLabel}>
        {label}
      </FieldLabel>
      <DropdownVariablePicker
        nodeId={nodeId}
        value={value}
        onChange={onChange}
        placeholder='Select shipment variable...'
        disabled={disabled}
        typeFilter={[VariableType.SHIPMENT]}
      />
      <FieldErrorLabel error={error} />
    </div>
  );
};
